import { zodResolver } from "@hookform/resolvers/zod";
import { Alert } from "@package/subframe/components/Alert";
import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithBackground } from "@package/subframe/components/IconWithBackground";
import { Select } from "@package/subframe/components/Select";
import { Switch } from "@package/subframe/components/Switch";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherArrowLeft,
  FeatherCopy,
  FeatherKey,
  FeatherShieldQuestion,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type FieldErrors, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useAddNurseryStaffCommandControllerAddStaff } from "@/generated/members/members";
import type {
  NurseryItemDto,
  UserMembershipDto,
  UserNurseryAccessDto,
} from "@/generated/model";
import { AddNurseryStaffDtoRole } from "@/generated/model";
import { EmploymentStatus } from "@/generated/model/employmentStatus";
import {
  useListNurseryLineManagerCandidates,
  useUpdateEmployeeKeyInformation,
} from "@/generated/people/people";
import { useListRoomsQueryControllerList } from "@/generated/rooms/rooms";
import { employmentStatusLabel } from "@/lib/people";
import {
  checkEmploymentDates,
  employmentDetailsRequest,
  employmentDetailsShape,
  hasEmploymentDetails,
} from "@/lib/people-key-information";
import { NURSERY_ROLE_DESCRIPTIONS } from "@/lib/roles";
import { generateTemporaryPassword } from "@/lib/temporary-password";

const STAFF_EMAIL_DOMAIN =
  import.meta.env.VITE_STAFF_EMAIL_DOMAIN || "naud-staff.local";

/** Radix Select items can't have an empty value, so "none" needs a stand-in. */
const NONE = "__none__";

const staffFormSchema = z
  .object({
    // The same rules as Key Information, which the employment details save
    // re-sends the names to.
    firstName: z
      .string()
      .trim()
      .min(1, "First name is required")
      .max(100, "First name must be 100 characters or fewer"),
    lastName: z
      .string()
      .trim()
      .min(1, "Last name is required")
      .max(100, "Last name must be 100 characters or fewer"),
    // The username seeds the staff-domain email when no address is given, so
    // it has to be something an address can be built from.
    username: z
      .string()
      .min(3, "Username must be at least 3 characters")
      .max(30, "Username must be less than 30 characters")
      .regex(
        /^[a-z0-9_]+$/,
        "Username can only contain lowercase letters, numbers, and underscores",
      ),
    email: z
      .string()
      .email("Invalid email address")
      .optional()
      .or(z.literal("")),
    role: z.enum(["MANAGER", "DEPUTY_MANAGER", "LEADER", "PRACTITIONER"]),
    nurseryId: z.string().min(1, "Location is required"),
    password: z.string().optional(),
    confirmPassword: z.string().optional(),
    temporaryPin: z.string().optional(),
    isActive: z.boolean(),
    ...employmentDetailsShape,
  })
  .refine(
    (data) => {
      if (data.password && data.password.length > 0) {
        return data.password.length >= 8;
      }
      return true;
    },
    {
      message: "Password must be at least 8 characters",
      path: ["password"],
    },
  )
  .refine(
    (data) => {
      if (data.password && data.password.length > 0) {
        return data.password === data.confirmPassword;
      }
      return true;
    },
    {
      message: "Passwords do not match",
      path: ["confirmPassword"],
    },
  )
  .superRefine(checkEmploymentDates);

type StaffFormData = z.infer<typeof staffFormSchema>;

// The username input keeps a non-standard id so password managers leave it alone.
const ERROR_FIELD_IDS: Partial<Record<keyof StaffFormData, string>> = {
  username: "staff_username",
};

interface StaffAdder {
  isPlatformAdmin: boolean;
  memberships: UserMembershipDto[];
  nurseryAccess: UserNurseryAccessDto[];
}

/** Mirrors the add-staff policy: platform admins, active group admins, and the nursery's managers. */
function canAddStaffAt(nursery: NurseryItemDto, caller: StaffAdder): boolean {
  if (mayAddManagerAt(nursery, caller)) {
    return true;
  }
  return caller.nurseryAccess.some(
    (access) => access.nurseryId === nursery.id && access.role === "MANAGER",
  );
}

/** Only a platform admin or an active group admin of the nursery's organisation adds a manager. */
function mayAddManagerAt(nursery: NurseryItemDto, caller: StaffAdder): boolean {
  if (caller.isPlatformAdmin) {
    return true;
  }
  return caller.memberships.some(
    (membership) =>
      membership.organisationId === nursery.organisationId &&
      membership.status === "ACTIVE" &&
      membership.role === "ADMIN",
  );
}

export function StaffForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isPlatformAdmin, memberships, nurseryAccess } = useAuth();
  const { nurseries, selectedNurseryId, setSelectedNursery } = useNursery();

  const [usernameManuallyEdited, setUsernameManuallyEdited] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");

  const addStaff = useAddNurseryStaffCommandControllerAddStaff();
  const updateKeyInformation = useUpdateEmployeeKeyInformation();

  const assignableNurseries = useMemo(
    () =>
      nurseries.filter((nursery) =>
        canAddStaffAt(nursery, { isPlatformAdmin, memberships, nurseryAccess }),
      ),
    [nurseries, isPlatformAdmin, memberships, nurseryAccess],
  );

  // The rules depend on the selected nursery and role: practitioners and
  // leaders on a PIN-enabled nursery sign in by PIN, so their password is
  // auto-generated and not required; the temporary PIN is only required when
  // the nursery has PIN login enabled.
  const schema = useMemo(
    () =>
      staffFormSchema.superRefine((data, ctx) => {
        const nursery = nurseries.find((n) => n.id === data.nurseryId);
        const nurseryPinEnabled = nursery?.pinLoginEnabled ?? false;
        const pinOnlyRole =
          data.role === "PRACTITIONER" || data.role === "LEADER";
        const passwordAutoGenerated = nurseryPinEnabled && pinOnlyRole;

        if (
          !passwordAutoGenerated &&
          (!data.password || data.password.length < 8)
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["password"],
            message: "Password must be at least 8 characters",
          });
        }

        if (nurseryPinEnabled && !/^\d{4}$/.test(data.temporaryPin ?? "")) {
          ctx.addIssue({
            code: "custom",
            path: ["temporaryPin"],
            message: "Temporary PIN must be exactly 4 digits",
          });
        }
      }),
    [nurseries],
  );

  const {
    handleSubmit,
    setValue,
    resetField,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<StaffFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      role: undefined,
      nurseryId: "",
      password: "",
      confirmPassword: "",
      temporaryPin: "",
      isActive: true,
      jobTitle: "",
      roomId: "",
      lineManagerUserId: "",
      employmentStatus: "",
      startDate: "",
      endDate: "",
    },
  });

  const watchedRole = watch("role");
  const watchedNurseryId = watch("nurseryId");
  const watchedFirstName = watch("firstName");
  const watchedLastName = watch("lastName");
  const watchedUsername = watch("username");
  const watchedEmail = watch("email");
  const watchedPassword = watch("password");
  const watchedConfirmPassword = watch("confirmPassword");
  const watchedTemporaryPin = watch("temporaryPin");
  const watchedIsActive = watch("isActive");
  const watchedJobTitle = watch("jobTitle");
  const watchedRoomId = watch("roomId");
  const watchedLineManagerUserId = watch("lineManagerUserId");
  const watchedEmploymentStatus = watch("employmentStatus");
  const watchedStartDate = watch("startDate");
  const watchedEndDate = watch("endDate");

  const assignedNursery = nurseries.find((n) => n.id === watchedNurseryId);
  const mayAddManager = assignedNursery
    ? mayAddManagerAt(assignedNursery, {
        isPlatformAdmin,
        memberships,
        nurseryAccess,
      })
    : false;
  const selectedNurseryPinEnabled = assignedNursery?.pinLoginEnabled ?? false;
  const isPinOnlyRole =
    watchedRole === "PRACTITIONER" || watchedRole === "LEADER";
  // Practitioners/leaders on a PIN-enabled nursery sign in by PIN, so hide the
  // password fields and auto-generate a password on submit. PIN controls only
  // appear when the selected nursery has PIN login switched on.
  const hidePasswordFields = selectedNurseryPinEnabled && isPinOnlyRole;
  const showTemporaryPin = selectedNurseryPinEnabled;
  const showEndDate = watchedEmploymentStatus === EmploymentStatus.LEFT;

  const roomsQuery = useListRoomsQueryControllerList(
    watchedNurseryId,
    undefined,
    { query: { enabled: !!watchedNurseryId } },
  );
  const lineManagersQuery = useListNurseryLineManagerCandidates(
    watchedNurseryId,
    { query: { enabled: !!watchedNurseryId } },
  );
  const activeRooms = (roomsQuery.data?.data ?? []).filter(
    (room) => room.isActive,
  );
  const lineManagers = lineManagersQuery.data?.data ?? [];
  const listsFailed = roomsQuery.isError || lineManagersQuery.isError;

  // Default to the current nursery when nurseries load, falling back to the
  // first one the caller can add staff to before a selection resolves
  useEffect(() => {
    if (watchedNurseryId) {
      return;
    }
    const defaultNursery =
      assignableNurseries.find((n) => n.id === selectedNurseryId) ??
      assignableNurseries[0];
    if (!defaultNursery) {
      return;
    }
    setValue("nurseryId", defaultNursery.id);
  }, [assignableNurseries, selectedNurseryId, watchedNurseryId, setValue]);

  // Scroll to first error field on validation failure
  const onFormError = useCallback((errors: FieldErrors<StaffFormData>) => {
    const firstErrorKey = Object.keys(errors)[0] as keyof StaffFormData;
    if (firstErrorKey) {
      const element = document.getElementById(
        ERROR_FIELD_IDS[firstErrorKey] ?? firstErrorKey,
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
    }
  }, []);

  // Generate username from first and last name
  const generateUsername = (firstName: string, lastName: string) => {
    return `${firstName}_${lastName}`
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, "")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  };

  const handleFirstNameChange = (value: string) => {
    setValue("firstName", value);
    if (!usernameManuallyEdited) {
      const lastName = watchedLastName || "";
      setValue("username", generateUsername(value, lastName));
    }
  };

  const handleLastNameChange = (value: string) => {
    setValue("lastName", value);
    if (!usernameManuallyEdited) {
      const firstName = watchedFirstName || "";
      setValue("username", generateUsername(firstName, value));
    }
  };

  const handleUsernameChange = (value: string) => {
    const cleanedValue = value.toLowerCase().replace(/[^a-z0-9_]/g, "");
    setValue("username", cleanedValue);
    setUsernameManuallyEdited(true);
  };

  const handleNurseryChange = (nurseryId: string) => {
    if (!nurseryId) {
      return;
    }
    if (nurseryId === watchedNurseryId) {
      return;
    }
    setValue("nurseryId", nurseryId);
    // Rooms and line managers belong to the nursery.
    setValue("roomId", "");
    setValue("lineManagerUserId", "");
    const nursery = nurseries.find((n) => n.id === nurseryId);
    const managerAllowed =
      nursery !== undefined &&
      mayAddManagerAt(nursery, { isPlatformAdmin, memberships, nurseryAccess });
    if (watchedRole === "MANAGER" && !managerAllowed) {
      resetField("role");
    }
  };

  const handleEmploymentStatusChange = (value: string) => {
    const status = value === NONE ? "" : (value as EmploymentStatus);
    if (status === watchedEmploymentStatus) {
      return;
    }
    setValue("employmentStatus", status);
    // Someone who has left shouldn't sign in, but they can be switched back on.
    if (status === EmploymentStatus.LEFT) {
      setValue("isActive", false);
      return;
    }
    // The end date only shows for Left, so a hidden one must not be saved.
    setValue("endDate", "");
  };

  const generateRandomPassword = () => {
    const password = generateTemporaryPassword();
    setGeneratedPassword(password);
    setValue("password", password);
    setValue("confirmPassword", password);
  };

  const copyPasswordToClipboard = () => {
    const password = watchedPassword || generatedPassword;
    if (password) {
      navigator.clipboard.writeText(password);
      toast.success("Password copied to clipboard");
    }
  };

  const addStaffMember = async (data: StaffFormData) => {
    try {
      // If email is empty, generate from username
      const email = data.email || `${data.username}@${STAFF_EMAIL_DOMAIN}`;

      // PIN-login staff don't set a password (they sign in by PIN), so mint a
      // strong random one to back their identity. Everyone else uses the
      // password they entered. Only send a temporary PIN when the nursery has
      // PIN login enabled.
      const nursery = nurseries.find((n) => n.id === data.nurseryId);
      const nurseryPinEnabled = nursery?.pinLoginEnabled ?? false;
      const pinOnlyRole =
        data.role === "PRACTITIONER" || data.role === "LEADER";
      const password =
        data.password && data.password.length > 0
          ? data.password
          : nurseryPinEnabled && pinOnlyRole
            ? generateTemporaryPassword()
            : data.password || "";

      const response = await addStaff.mutateAsync({
        nurseryId: data.nurseryId,
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
          email,
          password,
          role: data.role as AddNurseryStaffDtoRole,
          isActive: data.isActive,
          temporaryPin: nurseryPinEnabled ? data.temporaryPin : undefined,
        },
      });
      return response.data;
    } catch (error: unknown) {
      let message = "Failed to add staff member";
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response
      ) {
        const responseData = error.response.data as Record<string, unknown>;
        if (responseData.message && typeof responseData.message === "string") {
          message = responseData.message;
        }
      } else if (error instanceof Error) {
        message = error.message;
      }
      toast.error(message);
      return null;
    }
  };

  /** Saves the Employment details box, if filled in, and says how adding went. */
  const saveEmploymentDetails = async (
    userId: string,
    userCreated: boolean,
    data: StaffFormData,
  ) => {
    // An existing account is only added to the nursery: its password, PIN,
    // status and profile are left as they were.
    if (!userCreated) {
      toast.info(
        "Added to this nursery. This email already had an account, so their password, PIN and details weren't changed.",
      );
      return;
    }
    if (!hasEmploymentDetails(data)) {
      toast.success("Employee added");
      return;
    }
    const { firstName, lastName } = data;
    try {
      await updateKeyInformation.mutateAsync({
        nurseryId: data.nurseryId,
        userId,
        data: employmentDetailsRequest({ firstName, lastName }, data),
      });
      toast.success("Employee added");
    } catch {
      toast.error("Employee added, but their employment details weren't saved");
    }
  };

  const onSubmit = async (data: StaffFormData) => {
    const added = await addStaffMember(data);
    if (!added) {
      return;
    }
    const userId = added.userId;
    if (!userId) {
      toast.error(
        "Employee added, but their profile couldn't be opened. Find them on the People page.",
      );
      return;
    }

    await saveEmploymentDetails(userId, added.userCreated === true, data);

    // The profile loads for the selected nursery, so it must be the one they joined.
    const joined = nurseries.find((n) => n.id === data.nurseryId);
    if (joined && joined.id !== selectedNurseryId) {
      setSelectedNursery(joined.id, joined.organisationId);
    }

    // Refetch before navigating: leaving this page cancels a refetch still in flight.
    await queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        typeof query.queryKey[0] === "string" &&
        (query.queryKey[0].includes("/members") ||
          query.queryKey[0].includes("/users") ||
          query.queryKey[0].includes("/people")),
      refetchType: "all",
    });

    navigate({ to: "/people/$userId", params: { userId } });
  };

  const isSaving =
    isSubmitting || addStaff.isPending || updateKeyInformation.isPending;

  return (
    <div className="flex w-full flex-col items-start bg-white animate-fade-in">
      <form
        onSubmit={handleSubmit(onSubmit, onFormError)}
        className="flex w-full flex-col items-start"
      >
        {/* Header */}
        <div className="flex w-full flex-col items-start gap-1 pb-8">
          <div className="flex w-full flex-wrap items-center gap-4">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <div className="flex w-full items-center gap-2">
                <IconButton
                  icon={<FeatherArrowLeft />}
                  onClick={() => navigate({ to: "/people" })}
                />
                <span className="grow shrink-0 basis-0 whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
                  Add employee
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-body font-body text-subtext-color">
                  Add a new employee to
                </span>
                {assignedNursery && (
                  <Badge variant="neutral">{assignedNursery.name}</Badge>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="neutral-primary"
                size="large"
                onClick={() => navigate({ to: "/people" })}
              >
                Cancel
              </Button>
              <Button type="submit" size="large" disabled={isSaving}>
                {isSaving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          {/* Personal Information Section */}
          <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-9">
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start">
                <span className="w-full text-heading-3 font-heading-3 text-default-font">
                  Personal information
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  This is how this member of staff will appear on the system
                </span>
              </div>
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full min-w-[448px] flex-col items-start gap-6">
                <div className="flex w-full items-start gap-6">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <TextField
                      className="h-auto w-full flex-none"
                      size="large"
                      label="First name"
                      error={!!errors.firstName}
                      helpText={errors.firstName?.message}
                    >
                      <TextField.Input
                        id="firstName"
                        placeholder="Enter first name"
                        value={watchedFirstName}
                        onChange={(e) => handleFirstNameChange(e.target.value)}
                      />
                    </TextField>
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <TextField
                      className="h-auto w-full flex-none"
                      size="large"
                      label="Last name"
                      error={!!errors.lastName}
                      helpText={errors.lastName?.message}
                    >
                      <TextField.Input
                        id="lastName"
                        placeholder="Enter last name"
                        value={watchedLastName}
                        onChange={(e) => handleLastNameChange(e.target.value)}
                      />
                    </TextField>
                  </div>
                </div>
                {/* Hidden honeypot fields to absorb browser autofill */}
                <div className="hidden" aria-hidden="true">
                  <input
                    type="text"
                    name="username"
                    autoComplete="username"
                    tabIndex={-1}
                  />
                  <input
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    tabIndex={-1}
                  />
                </div>
                <div className="flex w-full items-start gap-6">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <TextField
                      className="h-auto w-full flex-none"
                      size="large"
                      label="Username"
                      error={!!errors.username}
                      helpText={errors.username?.message}
                    >
                      <TextField.Input
                        id="staff_username"
                        name="staff_username_nofill"
                        placeholder="Enter username"
                        value={watchedUsername}
                        onChange={(e) => handleUsernameChange(e.target.value)}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        data-form-type="other"
                        data-1p-ignore="true"
                        data-lpignore="true"
                        data-bwignore="true"
                      />
                    </TextField>
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <TextField
                      className="h-auto w-full flex-none"
                      size="large"
                      label="Email (optional)"
                      error={!!errors.email}
                      helpText={errors.email?.message}
                    >
                      <TextField.Input
                        id="email"
                        type="email"
                        placeholder="Enter email"
                        value={watchedEmail || ""}
                        onChange={(e) => setValue("email", e.target.value)}
                      />
                    </TextField>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Role and Permissions Section */}
          <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-9">
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start">
                <span className="w-full text-heading-3 font-heading-3 text-default-font">
                  Role and permissions
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  Give your staff members the right level of access
                </span>
              </div>
              <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-6 py-6">
                <div className="flex flex-col items-start gap-4">
                  <IconWithBackground
                    size="medium"
                    icon={<FeatherShieldQuestion />}
                  />
                  <span className="text-body-bold font-body-bold text-default-font">
                    Roles explained
                  </span>
                </div>
                <span className="text-body font-body text-subtext-color">
                  Roles allow you to control the level of functionality
                  available to the user.
                </span>
              </div>
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full items-center gap-6">
                <Select
                  className="h-auto grow shrink-0 basis-0 max-w-[49%]"
                  data-testid="role-select"
                  label="Role"
                  placeholder="Select a role"
                  size="large"
                  // Empty string, not undefined, so a cleared role shows the placeholder.
                  value={watchedRole ?? ""}
                  onValueChange={(value) => {
                    if (value) {
                      setValue("role", value as StaffFormData["role"]);
                    }
                  }}
                  error={!!errors.role}
                  helpText={errors.role?.message}
                >
                  {mayAddManager && (
                    <Select.Item value="MANAGER">Manager</Select.Item>
                  )}
                  <Select.Item value="DEPUTY_MANAGER">
                    Deputy Manager
                  </Select.Item>
                  <Select.Item value="LEADER">Leader</Select.Item>
                  <Select.Item value="PRACTITIONER">Practitioner</Select.Item>
                </Select>
                <div className="flex min-h-10 flex-col items-start justify-end">
                  <span className="text-body font-body text-subtext-color">
                    {watchedRole
                      ? NURSERY_ROLE_DESCRIPTIONS[watchedRole]
                      : "Select a role to see its description."}
                  </span>
                </div>
              </div>
              <div className="flex w-full items-center gap-6">
                <Select
                  data-testid="assigned-location-select"
                  className="h-auto grow shrink-0 basis-0 max-w-[49%]"
                  label="Assigned location"
                  placeholder="Select a location"
                  size="large"
                  value={watchedNurseryId}
                  onValueChange={handleNurseryChange}
                  error={!!errors.nurseryId}
                  helpText={errors.nurseryId?.message}
                >
                  {assignableNurseries.map((nursery) => (
                    <Select.Item key={nursery.id} value={nursery.id}>
                      {nursery.name}
                    </Select.Item>
                  ))}
                </Select>
                <div className="flex h-10 flex-col items-start justify-end">
                  <span className="text-body font-body text-subtext-color">
                    Select which nursery this staff member can access.
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Employment Details Section */}
          <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-9">
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start">
                <span className="w-full text-heading-3 font-heading-3 text-default-font">
                  Employment details
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  Optional. You can add or change these later on their profile.
                </span>
              </div>
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full min-w-[448px] flex-col items-start gap-6">
                {listsFailed && (
                  <Alert
                    variant="error"
                    title="Couldn't load the rooms or line managers"
                    description="Reload the page to try again."
                  />
                )}
                <div className="flex w-full items-start gap-6">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <TextField
                      className="h-auto w-full flex-none"
                      size="large"
                      label="Job title"
                      error={!!errors.jobTitle}
                      helpText={errors.jobTitle?.message}
                    >
                      <TextField.Input
                        id="jobTitle"
                        placeholder="Enter job title"
                        value={watchedJobTitle}
                        onChange={(e) => setValue("jobTitle", e.target.value)}
                      />
                    </TextField>
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <Select
                      data-testid="room-select"
                      className="h-auto w-full flex-none"
                      label="Team / room"
                      size="large"
                      value={watchedRoomId || NONE}
                      onValueChange={(value) =>
                        setValue("roomId", value === NONE ? "" : value)
                      }
                    >
                      <Select.Item value={NONE}>No room</Select.Item>
                      {activeRooms.map((room) => (
                        <Select.Item key={room.id} value={room.id}>
                          {room.name}
                        </Select.Item>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className="flex w-full items-start gap-6">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <Select
                      data-testid="line-manager-select"
                      className="h-auto w-full flex-none"
                      label="Line manager"
                      size="large"
                      value={watchedLineManagerUserId || NONE}
                      onValueChange={(value) =>
                        setValue(
                          "lineManagerUserId",
                          value === NONE ? "" : value,
                        )
                      }
                      helpText="Managers and deputy managers at this nursery."
                    >
                      <Select.Item value={NONE}>No line manager</Select.Item>
                      {lineManagers.map((candidate) => (
                        <Select.Item
                          key={candidate.userId}
                          value={candidate.userId}
                        >
                          {candidate.name || candidate.email}
                        </Select.Item>
                      ))}
                    </Select>
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <Select
                      data-testid="employment-status-select"
                      className="h-auto w-full flex-none"
                      label="Employment status"
                      size="large"
                      value={watchedEmploymentStatus || NONE}
                      onValueChange={handleEmploymentStatusChange}
                    >
                      <Select.Item value={NONE}>Not recorded</Select.Item>
                      {Object.values(EmploymentStatus).map((status) => (
                        <Select.Item key={status} value={status}>
                          {employmentStatusLabel(status)}
                        </Select.Item>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className="flex w-full items-start gap-6">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <TextField
                      className="h-auto w-full flex-none"
                      size="large"
                      label="Start date"
                      error={!!errors.startDate}
                      helpText={errors.startDate?.message}
                    >
                      <TextField.Input
                        id="startDate"
                        type="date"
                        value={watchedStartDate}
                        onChange={(e) => setValue("startDate", e.target.value)}
                      />
                    </TextField>
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    {showEndDate && (
                      <TextField
                        className="h-auto w-full flex-none"
                        size="large"
                        label="End date"
                        error={!!errors.endDate}
                        helpText={errors.endDate?.message}
                      >
                        <TextField.Input
                          id="endDate"
                          type="date"
                          value={watchedEndDate}
                          onChange={(e) => setValue("endDate", e.target.value)}
                        />
                      </TextField>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Security Section */}
          <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-9">
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start">
                <span className="w-full text-heading-3 font-heading-3 text-default-font">
                  Security
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  {hidePasswordFields
                    ? "This staff member signs in with a PIN. Set a temporary PIN they'll replace on their first login."
                    : "Set the password for this staff member"}
                </span>
              </div>
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full min-w-[448px] flex-col items-start gap-6">
                {!hidePasswordFields && (
                  <>
                    <div className="flex w-full flex-col items-start gap-1">
                      <TextField
                        className="h-auto w-full flex-none"
                        size="large"
                        label="Password"
                        helpText={
                          errors.password?.message ||
                          "Minimum 8 characters required."
                        }
                        error={!!errors.password}
                      >
                        <TextField.Input
                          id="password"
                          type="password"
                          placeholder="Enter new password"
                          value={watchedPassword || ""}
                          onChange={(e) => setValue("password", e.target.value)}
                        />
                      </TextField>
                    </div>
                    <div className="flex w-full flex-col items-start gap-1">
                      <TextField
                        className="h-auto w-full flex-none"
                        size="large"
                        label="Confirm password"
                        error={!!errors.confirmPassword}
                        helpText={errors.confirmPassword?.message}
                      >
                        <TextField.Input
                          id="confirmPassword"
                          type="password"
                          placeholder="Confirm new password"
                          value={watchedConfirmPassword || ""}
                          onChange={(e) =>
                            setValue("confirmPassword", e.target.value)
                          }
                        />
                      </TextField>
                    </div>
                    <div className="flex w-full items-start justify-between">
                      <Button
                        type="button"
                        variant="neutral-primary"
                        icon={<FeatherKey />}
                        onClick={generateRandomPassword}
                      >
                        Generate password
                      </Button>
                      <Button
                        type="button"
                        variant="brand-tertiary"
                        icon={<FeatherCopy />}
                        onClick={copyPasswordToClipboard}
                        disabled={!watchedPassword && !generatedPassword}
                      >
                        Copy password
                      </Button>
                    </div>
                  </>
                )}

                {showTemporaryPin && (
                  <div
                    className={`flex w-full flex-col items-start gap-1 ${
                      hidePasswordFields
                        ? ""
                        : "border-t border-solid border-secondary-beige-200 pt-6"
                    }`}
                  >
                    <TextField
                      className="h-auto w-full flex-none"
                      size="large"
                      label="Temporary PIN"
                      error={!!errors.temporaryPin}
                      helpText={
                        errors.temporaryPin?.message ||
                        "4-digit PIN for shared-device login. Share it with the staff member — they'll choose their own PIN on their first login."
                      }
                    >
                      <TextField.Input
                        id="temporaryPin"
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="4-digit temporary PIN"
                        value={watchedTemporaryPin || ""}
                        onChange={(e) =>
                          setValue(
                            "temporaryPin",
                            e.target.value.replace(/\D/g, "").slice(0, 4),
                          )
                        }
                      />
                    </TextField>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Account Status Section */}
          <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-9">
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start">
                <span className="w-full text-heading-3 font-heading-3 text-default-font">
                  Account status
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  Control whether this staff member can access the system
                </span>
              </div>
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-secondary-beige-200 bg-default-background px-6 py-6">
              <div className="flex w-full items-center gap-4">
                <div className="flex grow shrink-0 basis-0 flex-col items-start">
                  <span className="grow shrink-0 basis-0 text-heading-3 font-heading-3 text-default-font">
                    Active account
                  </span>
                  <span className="w-full text-body font-body text-subtext-color">
                    Staff member can access the system and perform their duties
                  </span>
                </div>
                <Switch
                  aria-label="Active account"
                  checked={watchedIsActive}
                  onCheckedChange={(checked) => setValue("isActive", checked)}
                />
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-secondary-beige-200 pt-6">
            <Button
              type="button"
              variant="neutral-primary"
              size="large"
              onClick={() => navigate({ to: "/people" })}
            >
              Cancel
            </Button>
            <Button type="submit" size="large" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
